import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AuthDialog from "@/v2/components/AuthDialog";

type OAuthNamespace = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};

function oauthApi(): OAuthNamespace {
  return (supabase.auth as unknown as { oauth: OAuthNamespace }).oauth;
}

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("authorization_id mancante nella richiesta.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        setNeedsAuth(true);
        return;
      }
      setNeedsAuth(false);
      const { data, error: detailsError } = await oauthApi().getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (detailsError) {
        setError(detailsError.message);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId, reloadKey]);

  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error: decideError } = approve
      ? await oauthApi().approveAuthorization(authorizationId)
      : await oauthApi().denyAuthorization(authorizationId);
    if (decideError) {
      setBusy(false);
      setError(decideError.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("Il server di autorizzazione non ha restituito un redirect.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "l'applicazione";

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-4">
        {error ? (
          <>
            <h1 className="text-xl font-semibold">Richiesta non valida</h1>
            <p className="text-sm text-slate-400">{error}</p>
          </>
        ) : needsAuth ? (
          <>
            <h1 className="text-xl font-semibold">Accedi per continuare</h1>
            <p className="text-sm text-slate-400">
              Devi accedere al tuo account Progetto Startup per autorizzare questa connessione.
            </p>
            <AuthDialog
              open
              onOpenChange={() => {}}
              onSuccess={() => setReloadKey((k) => k + 1)}
              defaultTab="signin"
            />
          </>
        ) : !details ? (
          <p className="text-sm text-slate-400">Caricamento…</p>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Collega {clientName} al tuo account</h1>
            <p className="text-sm text-slate-400">
              {clientName} potrà leggere il tuo Reality Check e il tuo Sprint e aggiungere note agli
              step, agendo come te. Puoi revocare l'accesso in qualsiasi momento.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => decide(true)}
                className="flex-1 rounded-xl bg-[#FF6B47] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                Autorizza
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => decide(false)}
                className="flex-1 rounded-xl border border-white/20 px-4 py-2.5 text-sm font-medium text-slate-200 disabled:opacity-50"
              >
                Rifiuta
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
